﻿import { Chart } from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import type { ChartData, ChartOptions } from 'chart.js';

export const Weather = (() => {
    interface IRawData {
        dataKey: string;
        timeS: string;
        tempOut: number;
        tempOutHi: number;
        tempOutLow: number;
        humidityOut: number;
        dewPoint: number;
        windSpeed: number;
        windDir: string;
        windRun: number;
        windHiSpeed: number;
        windHiDir: string;
        windChill: number;
        heatIndex: number;
        thwIndex: number;
        thswIndex: number;
        barometer: number;
        rain: number;
        rainRate: number;
        solarRadiation: number;
        solarEnergy: number;
        solarRadiationHi: number;
        uvIndex: number;
        uvDose: number;
        uvHi: number;
        heatDd: number;
        coolDd: number;
        inTemp: number;
        inHumidity: number;
        inDew: number;
        inHeat: number;
        inEmc: number;
        inAirDensity: number;
        et: number;
        windSamp: number;
        windTx: boolean;
        issRecept: number;
        arcInt: number;
    };

    interface IDayData {
        day: number;
        month: number;
        year: number;
        rain: number;
        maxTemp: number;
        minTemp: number;
        minMaxTemp: number;
        maxMinTemp: number;
        avgTemp: number;
        avgMaxTemp: number;
        avgMinTemp: number;
        avgSolar: number;
        avgUv: number;
        maxSolar: number;
        maxUv: number;
        timeS: string;
    };

    interface IYearData {
        year: number;
        rainYear: number;
        months: Array<IMonthData>;
    };

    interface IMonthData {
        month: number;
        year: number;
        rain: number;
        maxTemp: number;
        minTemp: number;
        minMaxTemp: number;
        maxMinTemp: number;
        avgTemp: number;
        avgMaxTemp: number;
        avgMinTemp: number;
        avgSolar: number;
        avgUv: number;
        maxSolar: number;
        maxUv: number;
    };

    interface IData {
        dataTimeUtc: string,
        dataTimeS: string,
        update: Array<IRawData>;
        week: Array<IRawData>;
        pastMonth: Array<IDayData>;
        yearly: Array<IYearData>;

    };

    var OutTempChart: Chart<'line', number[], Date> | null = null;
    var OutTempDayChart: Chart<'line', number[], Date> | null = null;
    var RainChart: Chart<'line', number[], Date> | null = null;
    var RainPastMonthChart: Chart<'bar', number[], Date> | null = null;
    var RainMonthlyChart: Chart<'bar', number[], number> | null = null;
    var BarometerChart: Chart<'line', number[], Date> | null = null;
    var SolarChart: Chart<'line', number[], Date> | null = null;
    var UvChart: Chart<'line', number[], Date> | null = null;
    var InTempChart: Chart<'line', number[], Date> | null = null;
    var InTempDayChart: Chart<'line', number[], Date> | null = null;
    var SolarAvgMonthlyChart: Chart<'bar', number[], number> | null = null;
    var UvAvgMonthlyChart: Chart<'bar', number[], number> | null = null;
    var SolarMaxMonthlyChart: Chart<'bar', number[], number> | null = null;
    var UvMaxMonthlyChart: Chart<'bar', number[], number> | null = null;
    var TempMaxMonthlyChart: Chart<'bar', number[], number> | null = null;
    var TempMinMaxMonthlyChart: Chart<'bar', number[], number> | null = null;
    var TempMinMonthlyChart: Chart<'bar', number[], number> | null = null;
    var TempMaxMinMonthlyChart: Chart<'bar', number[], number> | null = null;
    var TempAvgMonthlyChart: Chart<'bar', number[], number> | null = null;
    var TempAvgMaxMonthlyChart: Chart<'bar', number[], number> | null = null;
    var TempAvgMinMonthlyChart: Chart<'bar', number[], number> | null = null;

    const MonthName = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const MonthLabels = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];


    var _data: IData;
    var _fullRefreshTime: Date = new Date();

    const BackgroundColours = [
        'rgba(255, 0, 0, 0.2)',
        'rgba(0, 0, 255, 0.2)',
        'rgba(210, 210, 0, 0.4)',
        'rgba(255, 0, 255, 0.2)',
        'rgba(0, 200, 0, 0.2)',
        'rgba(0, 180, 0, 0.7)',
        'rgba(0, 255, 255, 0.2)',
        'rgba(180, 180, 180, 0.2)',
        'rgba(150, 255, 150, 0.2)',
        'rgba(150, 150, 255, 0.2)',
        'rgba(255, 150, 150, 0.2)'
    ];

    const BorderColours = [
        'rgba(255, 0, 0, 1)',
        'rgba(0, 0, 255, 1)',
        'rgba(210, 210, 0, 1)',
        'rgba(255, 0, 255, 1)',
        'rgba(0, 200, 0, 1)',
        'rgba(0, 255, 0, 1)',
        'rgba(0, 255, 255, 1)',
        'rgba(180, 180, 180, 1)',
        'rgba(150, 255, 150, 1)',
        'rgba(150, 150, 255, 1)',
        'rgba(255, 150, 150, 1)'
    ];


    const InitAsync = async () => {
        window.setInterval(ChartAsync, 1000 * 60);  // 1 minute
        await ChartAsync();
    };


    const ChartAsync = async () => {
        var refreshTime = document.getElementById('RefreshTime');
        document.getElementById('Refresh')?.classList.remove('d-none');

        var headers = sil.Ajax.Headers();
        var dataStart = Date.now();

        var data = {
            lastUpdateUtc: refreshTime?.dataset.dataTimeUtc ?? ''
        };

        const response = await fetch('/Home/GetData', {
            method: 'POST',
            headers,
            body: new URLSearchParams(data)
        });

        if (!response.ok)
            throw new Error(await response.text());

        const d: IData = await response.json();

        var dataEnd = Date.now();
        var dataTime = (dataEnd - dataStart) / 1000;

        if (d.update == null)
            _data = d;
        else {
            _data.dataTimeUtc = d.dataTimeUtc;
            _data.dataTimeS = d.dataTimeS;
            d.update.forEach(el => {
                if (_data.week.at(-1)?.timeS != el.timeS) {
                    _data.week.push(el);

                    var lastMonth = _data.pastMonth.at(-1);
                    if (lastMonth) {
                        lastMonth.rain += el.rain;
                    }

                    var lastYear = _data.yearly.at(-1);
                    if (lastYear) {
                        lastYear.rainYear += el.rain;

                        var lastYearlyMonth = lastYear.months.at(-1);
                        if (lastYearlyMonth) {
                            if (lastYearlyMonth.maxTemp < el.tempOutHi)
                                lastYearlyMonth.maxTemp = el.tempOutHi;

                            if (lastYearlyMonth.minTemp > el.tempOutLow)
                                lastYearlyMonth.minTemp = el.tempOutLow;

/*                            if (lastYearlyMonth.minMaxTemp > el.tempOutHi)
                                lastYearlyMonth.minMaxTemp = el.tempOutHi;

                            if (lastYearlyMonth.maxMinTemp < el.tempOutLow)
                                lastYearlyMonth.maxMinTemp = el.tempOutLow;*/

                            lastYearlyMonth.rain += el.rain;
                        }
                    }
                }
            });
        }

        Charts(_data);
        document.getElementById('Refresh')?.classList.add('d-none');

        var chartTime = (Date.now() - dataEnd) / 1000;

        if (refreshTime) {
            var mins = (Date.now() - _fullRefreshTime.getTime()) / (1000 * 60);  // minutes

            if (mins >= 60 * 8) {   // 8 hours
                delete refreshTime.dataset.dataTimeUtc;
                _fullRefreshTime = new Date();
            }
            else
                refreshTime.dataset.dataTimeUtc = d.dataTimeUtc;

            var span = document.createElement('span');
            span.classList.add('font-size-50', 'ms-1');
            span.innerHTML = '<div class="d-inline-block"><div>[' + dataTime.toFixed(1) + ']</div><div>[' + chartTime.toFixed(1) + ']</div></div>';
            refreshTime.appendChild(span);
        }

    };

    const Pad = (n: any, width: number, z?: any) => {
        z = z || '0';
        n = n + '';
        return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
    };

    const Charts = (data: IData) => {
        //        var maxTime = new Date(data.week[data.week.length - 1].timeS);
        var maxTime = new Date(data.dataTimeS);
        var now = new Date();

        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        var maxTimeS = maxTime.getDate() + " " + months[maxTime.getMonth()] + " " + maxTime.getFullYear() + " " + maxTime.getHours() + ":" + Pad(maxTime.getMinutes(), 2);
        var nowS = now.getDate() + " " + months[now.getMonth()] + " " + now.getFullYear() + " " + now.getHours() + ":" + Pad(now.getMinutes(), 2) + ":" + Pad(now.getSeconds(), 2);

        var dataTime = document.getElementById('DataTime');
        if (dataTime)
            dataTime.innerHTML = maxTimeS;
        var refreshTime = document.getElementById('RefreshTime');
        if (refreshTime)
            refreshTime.innerHTML = nowS;

        OutsideTemp(data.week);
        Rain(data);
        RainPastMonth(data);
        RainMonthly(data);
        Barometer(data.week);
        Solar(data.week);
        Uv(data.week);
        InsideTemp(data.week);


        SolarAvgMonthly(data);
        UvAvgMonthly(data);
        SolarMaxMonthly(data);
        UvMaxMonthly(data);

        TempMaxMonthly(data);
        TempMinMaxMonthly(data);
        TempMinMonthly(data);
        TempMaxMinMonthly(data);
        TempAvgMonthly(data);
        TempAvgMaxMonthly(data);
        TempAvgMinMonthly(data);
    };

    const OutsideTemp = (data: Array<IRawData>) => {

        var outTempData: ChartData<'line', number[], Date> = {
            labels: [],
            datasets: []
        };

        var outTempDayData: ChartData<'line', number[], Date> = {
            labels: [],
            datasets: []
        };

        var outTemp: Array<number> = [];
        var outHumidity: Array<number> = [];
        var outMaxTemp = [];
        var outMinTemp = [];

        var outTempDay: Array<number> = [];
        var outHumidityDay: Array<number> = [];
        var outMaxTempDay = [];
        var outMinTempDay = [];

        var maxTime = new Date(0);
        var maxTemp = -100;
        var maxReadingTime = new Date(0);
        var maxIndex = -1;
        var maxIndexDay = -1;

        var minTime = new Date(0);
        var minTemp = 100;
        var minReadingTime = new Date(0);
        var minIndex = -1;
        var minIndexDay = -1;

        var dayIndex = -1;

        var lastDay = new Date();
        lastDay.setDate((new Date()).getDate() - 1);
        var index = 0;

        data.forEach(el => {
            var current = new Date(el.timeS);

            outTempData.labels!.push(current);
            outTemp.push(el.tempOut);
            outHumidity.push(el.humidityOut);
            outMaxTemp.push();
            outMinTemp.push();

            if (current >= lastDay) {
                outTempDayData.labels!.push(current);
                outTempDay.push(el.tempOut);
                outHumidityDay.push(el.humidityOut);
                outMaxTempDay.push();
                outMinTempDay.push();
                dayIndex++;
            }

            if (maxTemp < el.tempOutHi || maxTime.getDate() !== current.getDate()) {

                if (maxTime === new Date(0))
                    maxTime = current;
                else
                    if (maxTime.getDate() !== current.getDate()) {
                        outMaxTemp[maxIndex] = maxTemp;
                        maxTime = current;
                        if (current >= lastDay)
                            outMaxTempDay[maxIndexDay] = maxTemp;
                    }

                maxTemp = el.tempOutHi;
                maxReadingTime = current;
                maxIndex = index;
                maxIndexDay = dayIndex;
            }

            if (minTemp > el.tempOutLow || minTime.getDate() !== current.getDate()) {

                if (minTime === new Date(0))
                    minTime = current;
                else
                    if (minTime.getDate() !== current.getDate()) {
                        outMinTemp[minIndex] = minTemp;
                        minTime = current;
                        if (current >= lastDay)
                            outMinTempDay[minIndexDay] = minTemp;
                    }

                minTemp = el.tempOutLow;
                minReadingTime = current;
                minIndex = index;
                minIndexDay = dayIndex;
            }

            index++;
        });

        outMaxTemp[maxIndex] = maxTemp;
        outMinTemp[minIndex] = minTemp;

        outMaxTempDay[maxIndexDay] = maxTemp;
        outMinTempDay[minIndexDay] = minTemp;


        outTempData.datasets.push({
            label: 'Out Temp',
            yAxisID: 'A',
            data: outTemp,
            pointRadius: 0,
            fill: true,
            backgroundColor: [
                'rgba(0, 180, 0, 0.2)'
            ],
            borderColor: [
                'rgba(0, 180, 0, 1)'
            ],
            borderWidth: [
                1
            ]
        });


        outTempData.datasets.push({
            label: 'Max Temp',
            yAxisID: 'A',
            data: outMaxTemp,
            pointRadius: 3,
            backgroundColor: [
                'rgba(255, 140, 255, 1)'
            ],
            borderColor: [
                'rgba(255, 140, 255, 1)'
            ],
            borderWidth: [
                2
            ],
            spanGaps: true/*,
            lineTension: 0*/
        });

        outTempData.datasets.push({
            label: 'Min Temp',
            yAxisID: 'A',
            data: outMinTemp,
            pointRadius: 3,
            backgroundColor: [
                'rgba(0, 200, 255, 1)'
            ],
            borderColor: [
                'rgba(0, 200, 255, 1)'
            ],
            borderWidth: [
                2
            ],
            spanGaps: true/*,
            lineTension: 0*/
        });

        outTempData.datasets.push({
            label: 'Out Hum',
            yAxisID: 'B',
            data: outHumidity,
            pointRadius: 0,
            fill: true,
            backgroundColor: [
                'rgba(255, 180, 180, 0.1)'
            ],
            borderColor: [
                'rgba(255, 180, 180, 0.7)'
            ],
            borderWidth: [
                1
            ]
        });


        outTempDayData.datasets.push({
            label: 'Out Temp',
            yAxisID: 'A',
            data: outTempDay,
            pointRadius: 0,
            fill: true,
            backgroundColor: [
                'rgba(0, 180, 0, 0.2)'
            ],
            borderColor: [
                'rgba(0, 180, 0, 1)'
            ],
            borderWidth: [
                1
            ]
        });


        outTempDayData.datasets.push({
            label: 'Max Temp',
            yAxisID: 'A',
            data: outMaxTempDay,
            pointRadius: 3,
            backgroundColor: [
                'rgba(255, 140, 255, 1)'
            ],
            borderColor: [
                'rgba(255, 140, 255, 1)'
            ],
            borderWidth: [
                2
            ],
            spanGaps: true,
            tension: 0
        });

        outTempDayData.datasets.push({
            label: 'Min Temp',
            yAxisID: 'A',
            data: outMinTempDay,
            pointRadius: 3,
            backgroundColor: [
                'rgba(0, 200, 255, 1)'
            ],
            borderColor: [
                'rgba(0, 200, 255, 1)'
            ],
            borderWidth: [
                2
            ],
            spanGaps: true,
            tension: 0
        });

        outTempDayData.datasets.push({
            label: 'Out Hum',
            yAxisID: 'B',
            data: outHumidityDay,
            pointRadius: 0,
            fill: true,
            backgroundColor: [
                'rgba(255, 180, 180, 0.1)'
            ],
            borderColor: [
                'rgba(255, 180, 180, 0.7)'
            ],
            borderWidth: [
                1
            ]
        });



        var min = 0;
        if (minTemp < -5)
            min = -10;
        if (minTemp < 0)
            min = -5;


        var options: ChartOptions<'line'> = {
            plugins: {
                title: {
                    display: true,
                    text: 'Today   Max: ' + maxTemp + ' @ ' + maxReadingTime.getHours() + ':' + Pad(maxReadingTime.getMinutes(), 2) + '    Min: ' + minTemp + ' @ ' + minReadingTime.getHours() + ':' + Pad(minReadingTime.getMinutes(), 2)
                        + '  Last: ' + data[data.length - 1].tempOut + '  ' + data[data.length - 1].humidityOut + '%'
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'day',
                        displayFormats: {
                            day: 'd MMM yyyy'
                        }
                    }
                },
				'A': {
					type: 'linear',
					position: 'right'
				},
				'B': {
                    type: 'linear',
                    position: 'left',
                    max: 100,
                    min: min
                }
            },
            aspectRatio: 1.3
        };

        var optionsDay: ChartOptions<'line'> = {
            plugins: {
                title: {
                    display: true,
                    text: 'Today   Max: ' + maxTemp + ' @ ' + maxReadingTime.getHours() + ':' + Pad(maxReadingTime.getMinutes(), 2) + '    Min: ' + minTemp + ' @ ' + minReadingTime.getHours() + ':' + Pad(minReadingTime.getMinutes(), 2)
                        + '  Last: ' + data[data.length - 1].tempOut + '  ' + data[data.length - 1].humidityOut + '%'
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'hour',
                    }
                },
                'A': {
                    type: 'linear',
                    position: 'right'
                },
                'B': {
                    type: 'linear',
                    position: 'left',
                    max: 100,
                    min: min
                }
            },
            aspectRatio: 1.3
        };

        if (OutTempChart === null) {
            var ctx = (document.getElementById('OutTemp') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                OutTempChart = new Chart(ctx, {
                    type: 'line',
                    data: outTempData,
                    options
                });
        }
        else {
            OutTempChart.data = outTempData;
            OutTempChart.options = options;
            OutTempChart.update();
        }


        if (OutTempDayChart === null) {
            var ctx = (document.getElementById('OutTempDay') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                OutTempDayChart = new Chart(ctx, {
                    type: 'line',
                    data: outTempDayData,
                    options: optionsDay
                });
        }
        else {
            OutTempDayChart.data = outTempDayData;
            OutTempDayChart.options = optionsDay;
            OutTempDayChart.update();
        }
    };



    const Rain = (data: IData) => {

        var rainData: ChartData<'line', number[], Date> = {
            labels: [],
            datasets: []
        };

        var rainAccData: ChartData<'line', number[], Date> = {
            labels: [],
            datasets: []
        };

        var rain: Array<number> = [];
        var rainAcc: Array<number> = [];

        var dayRain = 0;
        var date: string | null;

        data.week.forEach(el => {
            rainData.labels!.push(new Date(el.timeS));
            rainAccData.labels!.push(new Date(el.timeS));

            rain.push(el.rain);

            if (date !== el.timeS.split('T')[0]) {
                date = el.timeS.split('T')[0];
                dayRain = 0;
            }

            dayRain += el.rain;
            dayRain = Math.round(dayRain * 10) / 10;
            rainAcc.push(dayRain);
        });

        rainData.datasets.push({
            label: 'Rain',
            data: rain,
            pointRadius: 0,
            backgroundColor: [
                'rgba(0, 0, 255, 0.2)'
            ],
            borderColor: [
                'rgba(0, 0, 255, 1)'
            ],
            borderWidth: [
                1
            ],
            fill: true
        });

        rainData.datasets.push({
            label: 'Rain Daily',
            data: rainAcc,
            pointRadius: 0,
            backgroundColor: [
                'rgba(150, 150, 255, 0.2)'
            ],
            borderColor: [
                'rgba(150, 150, 255, 1)'
            ],
            borderWidth: [
                1
            ],
            fill: true
        });

        var options: ChartOptions<'line'> = {
            plugins: {
                title: {
                    display: true,
                    text: 'Rain Today: ' + dayRain + 'mm'
                }
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'day',
                        displayFormats: {
                            day: 'd MMM yyyy'
                        }
                    }
                }
            },
            aspectRatio: 1.5
        };

        if (RainChart === null) {
            var ctx = (document.getElementById('Rain') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                RainChart = new Chart(ctx, {
                    type: 'line',
                    data: rainData,
                    options: options
                });
        }
        else {
            RainChart.data = rainData;
            RainChart.options = options;
            RainChart.update();
        }

    };

    const RainPastMonth = (data: IData) => {

        var rainPastMonth: ChartData<'bar', number[], Date> = {
            labels: [],
            datasets: []
        };

        var rain: Array<number> = [];

        data.pastMonth.forEach(el => {
            rainPastMonth.labels!.push(new Date(el.timeS));

            rain.push(el.rain);
        });

        rainPastMonth.datasets.push({
            label: 'Rain',
            data: rain,
            backgroundColor: [
                'rgba(0, 0, 255, 0.2)'
            ],
            borderColor: [
                'rgba(0, 0, 255, 1)'
            ],
            borderWidth: [
                1
            ]
        });


        var options: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: 'Rain Past Month'
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'day',
                        displayFormats: {
                            day: 'd MMM yyyy'
                        }
                    }
                }
            },
            aspectRatio: 1.5
        };

        if (RainPastMonthChart === null) {
            var ctx = (document.getElementById('RainPastMonth') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                RainPastMonthChart = new Chart(ctx, {
                    type: 'bar',
                    data: rainPastMonth,
                    options: options
                });
        }
        else {
            RainPastMonthChart.data = rainPastMonth;
            RainPastMonthChart.options = options;
            RainPastMonthChart.update();
        }
    };

    const RainMonthly = (data: IData) => {

        var rainMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var rainA: Array<number> = [];
        var count = 0;

        var yearly = 0;
        var title = 'Rain: ';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                rainA[el.month - 1] = el.rain;
                yearly += el.rain;
            });

            rainMonth.datasets.push({
                label: value.year.toString(),
                data: rainA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            yearly = Math.round(yearly * 10) / 10;

            title += value.year + ': ' + yearly + 'mm    ';

            count++;

            rainA = [];
            yearly = 0;
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.5
        };

        if (RainMonthlyChart === null) {
            var ctx = (document.getElementById('RainYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                RainMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: rainMonth,
                    options: optionsA
                });
        }
        else {
            RainMonthlyChart.data = rainMonth;
            RainMonthlyChart.options = optionsA;
            RainMonthlyChart.update();
        }

    };

    const Barometer = (data: Array<IRawData>) => {

        var barometerData: ChartData<'line', number[], Date> = {
            labels: [],
            datasets: []
        };

        var barometer: Array<number> = [];

        data.forEach(el => {
            barometerData.labels!.push(new Date(el.timeS));
            barometer.push(el.barometer);
        });

        barometerData.datasets.push({
            label: 'Barometer',
            data: barometer,
            pointRadius: 0,
            backgroundColor: [
                'rgba(180, 0, 180, 0.2)'
            ],
            borderColor: [
                'rgba(180, 0, 180, 1)'
            ],
            borderWidth: [
                1
            ],
            fill: true
        });

        var options: ChartOptions<'line'> = {
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'day',
                        displayFormats: {
                            day: 'd MMM yyyy'
                        }
                    }
                }
            }
        };

        if (BarometerChart === null) {
            var ctx = (document.getElementById('Barometer') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                BarometerChart = new Chart(ctx, {
                    type: 'line',
                    data: barometerData,
                    options: options
                });
        }
        else {
            BarometerChart.data = barometerData;
            BarometerChart.options = options;
            BarometerChart.update();
        }

    };

   /* const TempHiLow = (data) => {
        var hiLowData = {
            labels: [],
            datasets: []
        };

        var outMaxTemp = [];
        var outMinTemp = [];

        var maxTemp = -100;
        var minTemp = 100;

        var last = null;

        $.each(data, (index, element) => {
            var current = new Date(element.timeS);

            if (last === null)
                last = current;

            if (last.getDate() !== current.getDate()) {
                hiLowData.labels.push(last.getDate() + ' ' + Weather.MonthName[last.getMonth()] + ' ' + last.getFullYear());
                outMaxTemp.push(maxTemp);
                outMinTemp.push(minTemp);

                last = current;
                maxTemp = -100;
                minTemp = 100;
            }

            if (maxTemp < element.tempOutHi)
                maxTemp = element.tempOutHi;

            if (minTemp > element.tempOutLow)
                minTemp = element.tempOutLow;
        });

        hiLowData.labels.push(last.getDate() + ' ' + Weather.MonthName[last.getMonth()] + ' ' + last.getFullYear());
        outMaxTemp.push(maxTemp);
        outMinTemp.push(minTemp);


        hiLowData.datasets.push({
            label: 'Max',
            data: outMaxTemp,
            backgroundColor: Weather.BackgroundColours[0],
            borderColor: Weather.BorderColours[0]
        });

        hiLowData.datasets.push({
            label: 'Min',
            data: outMinTemp,
            backgroundColor: Weather.BackgroundColours[1],
            borderColor: Weather.BorderColours[1]
        });

        var optionsA = {
            plugins: {
                title: {
                    display: true,
                    text: 'Max & Min'
                }
            },
            scales: {
                y: {
                    type: 'linear'
                }
            }
        };

        if (Weather.OutTempHiLowChart === null) {
            var ctx = document.getElementById('OutTempHiLow').getContext('2d');
            Weather.OutTempHiLowChart = new Chart(ctx, {
                type: 'bar',
                data: hiLowData,
                options: optionsA
            });
        }
        else {
            Weather.OutTempHiLowChart.data = hiLowData;
            Weather.OutTempHiLowChart.options = optionsA;
            Weather.OutTempHiLowChart.update();
        }
    };*/

    const Solar = (data: Array<IRawData>) => {

        var solarData: ChartData<'line', number[], Date> = {
            labels: [],
            datasets: []
        };

        var solar: Array<number> = [];

        data.forEach(el => {
            solarData.labels!.push(new Date(el.timeS));
            solar.push(el.solarRadiationHi);
        });

        solarData.datasets.push({
            label: 'Solar Hi',
            data: solar,
            pointRadius: 0,
            backgroundColor: [
                'rgba(180, 180, 0, 0.2)'
            ],
            borderColor: [
                'rgba(180, 180, 0, 1)'
            ],
            borderWidth: [
                1
            ],
            fill: true
        });

        var options: ChartOptions<'line'> = {
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'day',
                        displayFormats: {
                            day: 'd MMM yyyy'
                        }
                    }
                }
            }
        };

        if (SolarChart === null) {
            var ctx = (document.getElementById('Solar') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                SolarChart = new Chart(ctx, {
                    type: 'line',
                    data: solarData,
                    options: options
                });
        }
        else {
            SolarChart.data = solarData;
            SolarChart.options = options;
            SolarChart.update();
        }

    };

    const Uv = (data: Array<IRawData>) => {

        var uvData: ChartData<'line', number[], Date> = {
            labels: [],
            datasets: []
        };

        var uv: Array<number> = [];
        var maxUv: Array<number> = [];

        var maxTime = new Date(0);
        var max = -100;
        var maxReadingTime = new Date(0);
        var maxIndex = -1;

        var index = 0;
        data.forEach(el => {
            var current = new Date(el.timeS);

            uvData.labels!.push(current);
            uv.push(el.uvHi);
            maxUv.push();

            if (max < el.uvHi || maxTime.getDate() !== current.getDate()) {

                if (maxTime === new Date(0))
                    maxTime = current;
                else
                    if (maxTime.getDate() !== current.getDate()) {
                        maxUv[maxIndex] = max;
                        maxTime = current;
                    }

                max = el.uvHi;
                maxReadingTime = current;
                maxIndex = index;
            }
            index++;
        });

        maxUv[maxIndex] = max;

        uvData.datasets.push({
            label: 'UV Hi',
            data: uv,
            pointRadius: 0,
            backgroundColor: [
                'rgba(180, 180, 0, 0.2)'
            ],
            borderColor: [
                'rgba(180, 180, 0, 1)'
            ],
            borderWidth: [
                1
            ],
            fill: true
        });

        uvData.datasets.push({
            label: 'Max UV',
            data: maxUv,
            pointRadius: 3,
            backgroundColor: [
                'rgba(180, 100, 0, 1)'
            ],
            borderColor: [
                'rgba(100, 100, 0, 1)'
            ],
            borderWidth: [
                2
            ],
            fill: false,
            spanGaps: true,
            tension: 0
        });


        var options: ChartOptions<'line'> = {
            plugins: {
                title: {
                    display: true,
                    text: 'Today Max: ' + max + " @ " + maxReadingTime.getHours() + ":" + Pad(maxReadingTime.getMinutes(), 2)
                }
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'day',
                        displayFormats: {
                            day: 'd MMM yyyy'
                        }
                    }
                }
            }
        };

        if (UvChart === null) {
            var ctx = (document.getElementById('UV') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                UvChart = new Chart(ctx, {
                    type: 'line',
                    data: uvData,
                    options: options
                });
        }
        else {
            UvChart.data = uvData;
            UvChart.options = options;
            UvChart.update();
        }
    };

    const InsideTemp = (data: Array<IRawData>) => {

        var inTempData: ChartData<'line', number[], Date> = {
            labels: [],
            datasets: []
        };

        var inTempDayData: ChartData<'line', number[], Date> = {
            labels: [],
            datasets: []
        };

        var inTemp: Array<number> = [];
        var inHumidity: Array<number> = [];
        var inMaxTemp: Array<number> = [];
        var inMinTemp: Array<number> = [];

        var inTempDay: Array<number> = [];
        var inHumidityDay: Array<number> = [];
        var inMaxTempDay: Array<number> = [];
        var inMinTempDay: Array<number> = [];

        var maxTime = new Date(0);
        var maxTemp = -100;
        var maxReadingTime = new Date(0);
        var maxIndex = -1;
        var maxIndexDay = -1;

        var minTime = new Date(0);
        var minTemp = 100;
        var minReadingTime = new Date(0);
        var minIndex = -1;
        var minIndexDay = -1;

        var dayIndex = -1;

        var lastDay = new Date();
        lastDay.setDate((new Date()).getDate() - 1);

        var index = 0;
        data.forEach(el => {
            var current = new Date(el.timeS);

            inTempData.labels!.push(current);
            inTemp.push(el.inTemp);
            inHumidity.push(el.inHumidity);
            inMaxTemp.push();
            inMinTemp.push();

            if (current >= lastDay) {
                inTempDayData.labels!.push(current);
                inTempDay.push(el.inTemp);
                inHumidityDay.push(el.inHumidity);
                inMaxTempDay.push();
                inMinTempDay.push();
                dayIndex++;
            }

            if (maxTemp < el.inTemp || maxTime.getDate() !== current.getDate()) {

                if (maxTime === new Date(0))
                    maxTime = current;
                else
                    if (maxTime.getDate() !== current.getDate()) {
                        inMaxTemp[maxIndex] = maxTemp;
                        maxTime = current;
                        if (current >= lastDay)
                            inMaxTempDay[maxIndexDay] = maxTemp;
                    }

                maxTemp = el.inTemp;
                maxReadingTime = current;
                maxIndex = index;
                maxIndexDay = dayIndex;
            }

            if (minTemp > el.inTemp || minTime.getDate() !== current.getDate()) {

                if (minTime === new Date(0))
                    minTime = current;
                else
                    if (minTime.getDate() !== current.getDate()) {
                        inMinTemp[minIndex] = minTemp;
                        minTime = current;
                        if (current >= lastDay)
                            inMinTempDay[minIndexDay] = minTemp;
                    }

                minTemp = el.inTemp;
                minReadingTime = current;
                minIndex = index;
                minIndexDay = dayIndex;
            }
            index++;
        });

        inMaxTemp[maxIndex] = maxTemp;
        inMinTemp[minIndex] = minTemp;

        inMaxTempDay[maxIndexDay] = maxTemp;
        inMinTempDay[minIndexDay] = minTemp;


        inTempData.datasets.push({
            label: 'In Temp',
            yAxisID: 'A',
            data: inTemp,
            pointRadius: 0,
            backgroundColor: [
                'rgba(0, 180, 0, 0.2)'
            ],
            borderColor: [
                'rgba(0, 180, 0, 1)'
            ],
            borderWidth: [
                1
            ],
            fill: true
        });


        inTempData.datasets.push({
            label: 'Max Temp',
            yAxisID: 'A',
            data: inMaxTemp,
            pointRadius: 3,
            backgroundColor: [
                'rgba(255, 140, 255, 1)'
            ],
            borderColor: [
                'rgba(255, 140, 255, 1)'
            ],
            borderWidth: [
                2
            ],
            spanGaps: true,
            tension: 0
        });

        inTempData.datasets.push({
            label: 'Min Temp',
            yAxisID: 'A',
            data: inMinTemp,
            pointRadius: 3,
            backgroundColor: [
                'rgba(0, 200, 255, 1)'
            ],
            borderColor: [
                'rgba(0, 200, 255, 1)'
            ],
            borderWidth: [
                2
            ],
            spanGaps: true,
            tension: 0
        });

        inTempData.datasets.push({
            label: 'In Hum',
            yAxisID: 'B',
            data: inHumidity,
            pointRadius: 0,
            backgroundColor: [
                'rgba(255, 180, 180, 0.1)'
            ],
            borderColor: [
                'rgba(255, 180, 180, 0.7)'
            ],
            borderWidth: [
                1
            ],
            fill: true
        });


        inTempDayData.datasets.push({
            label: 'In Temp',
            yAxisID: 'A',
            data: inTempDay,
            pointRadius: 0,
            backgroundColor: [
                'rgba(0, 180, 0, 0.2)'
            ],
            borderColor: [
                'rgba(0, 180, 0, 1)'
            ],
            borderWidth: [
                1
            ],
            fill: true
        });

        inTempDayData.datasets.push({
            label: 'Max Temp',
            yAxisID: 'A',
            data: inMaxTempDay,
            pointRadius: 3,
            backgroundColor: [
                'rgba(0, 200, 255, 1)'
            ],
            borderColor: [
                'rgba(0, 200, 255, 1)'
            ],
            borderWidth: [
                2
            ],
            spanGaps: true,
            tension: 0
        });


        inTempDayData.datasets.push({
            label: 'Min Temp',
            yAxisID: 'A',
            data: inMinTempDay,
            pointRadius: 3,
            backgroundColor: [
                'rgba(0, 200, 255, 1)'
            ],
            borderColor: [
                'rgba(0, 200, 255, 1)'
            ],
            borderWidth: [
                2
            ],
            spanGaps: true,
            tension: 0
        });


        inTempDayData.datasets.push({
            label: 'In Hum',
            yAxisID: 'B',
            data: inHumidityDay,
            pointRadius: 0,
            backgroundColor: [
                'rgba(255, 180, 180, 0.1)'
            ],
            borderColor: [
                'rgba(255, 180, 180, 0.7)'
            ],
            borderWidth: [
                1
            ],
            fill: true
        });

        var min = 0;
        if (minTemp < -5)
            min = -10;
        if (minTemp < 0)
            min = -5;


        var options: ChartOptions<'line'> = {
            plugins: {
                title: {
                    display: true,
                    text: 'Today   Max: ' + maxTemp + ' @ ' + maxReadingTime.getHours() + ':' + Pad(maxReadingTime.getMinutes(), 2) + '    Min: ' + minTemp + ' @ ' + minReadingTime.getHours() + ':' + Pad(minReadingTime.getMinutes(), 2)
                        + '  Last: ' + data[data.length - 1].inTemp + '  ' + data[data.length - 1].inHumidity + '%'
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'day',
                        displayFormats: {
                            day: 'd MMM yyyy'
                        }
                    }
                },
                'A': {
                    type: 'linear',
                    position: 'right'
                }, 
                'B': {
                    type: 'linear',
                    position: 'left',
                    max: 100,
                    min: min
                }
            },
            aspectRatio: 1.3
        };

        if (InTempChart === null) {
            var ctx = (document.getElementById('InTemp') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                InTempChart = new Chart(ctx, {
                    type: 'line',
                    data: inTempData,
                    options: options
                });
        }
        else {
            InTempChart.data = inTempData;
            InTempChart.options = options;
            InTempChart.update();
        }

        var optionsDay: ChartOptions<'line'> = {
            plugins: {
                title: {
                    display: true,
                    text: 'Today   Max: ' + maxTemp + ' @ ' + maxReadingTime.getHours() + ':' + Pad(maxReadingTime.getMinutes(), 2) + '    Min: ' + minTemp + ' @ ' + minReadingTime.getHours() + ':' + Pad(minReadingTime.getMinutes(), 2)
                        + '  Last: ' + data[data.length - 1].inTemp + '  ' + data[data.length - 1].inHumidity + '%'
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'hour',
                    }
                },
                'A': {
                    type: 'linear',
                    position: 'right'
                },
                'B': {
                    type: 'linear',
                    position: 'left',
                    max: 100,
                    min: min
                }
            },
            aspectRatio: 1.3
        };

        if (InTempChart === null) {
            var ctx = (document.getElementById('InTemp') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                InTempChart = new Chart(ctx, {
                    type: 'line',
                    data: inTempData,
                    options: options
                });
        }
        else {
            InTempChart.data = inTempData;
            InTempChart.options = options;
            InTempChart.update();
        }



        if (InTempDayChart === null) {
            var ctx = (document.getElementById('InTempDay') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                InTempDayChart = new Chart(ctx, {
                    type: 'line',
                    data: inTempDayData,
                    options: optionsDay
                });
        }
        else {
            InTempDayChart.data = inTempDayData;
            InTempDayChart.options = optionsDay;
            InTempDayChart.update();
        }
    };


    const SolarAvgMonthly = (data: IData) => {

        var solarMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var solarA: Array<number> = [];
        var count = 0;

        var title = 'Avg Solar';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                solarA[el.month - 1] = Math.round(el.avgSolar);
            });

            solarMonth.datasets.push({
                label: value.year.toString(),
                data: solarA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            count++;

            solarA = [];
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.6
        };

        if (SolarAvgMonthlyChart === null) {
            var ctx = (document.getElementById('SolarAvgYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                SolarAvgMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: solarMonth,
                    options: optionsA
                });
        }
        else {
            SolarAvgMonthlyChart.data = solarMonth;
            SolarAvgMonthlyChart.options = optionsA;
            SolarAvgMonthlyChart.update();
        }

    };

    const UvAvgMonthly = (data: IData) => {

        var uvMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var uvA: Array<number> = [];
        var count = 0;

        var title = 'Avg UV';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                uvA[el.month - 1] = Math.round(el.avgUv * 10) / 10;
            });

            uvMonth.datasets.push({
                label: value.year.toString(),
                data: uvA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            count++;

            uvA = [];
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.6
        };

        if (UvAvgMonthlyChart === null) {
            var ctx = (document.getElementById('UvAvgYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                UvAvgMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: uvMonth,
                    options: optionsA
                });
        }
        else {
            UvAvgMonthlyChart.data = uvMonth;
            UvAvgMonthlyChart.options = optionsA;
            UvAvgMonthlyChart.update();
        }

    };

    const SolarMaxMonthly = (data: IData) => {

        var solarMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var solarA: Array<number> = [];
        var count = 0;

        var yearMax = -100;
        var title = 'Max Solar   ';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                solarA[el.month - 1] = el.maxSolar;
                if (el.maxSolar > yearMax)
                    yearMax = el.maxSolar;
            });

            solarMonth.datasets.push({
                label: value.year.toString(),
                data: solarA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            title += value.year + ': ' + yearMax + '    ';

            count++;

            solarA = [];
            yearMax = -100;
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.6
        };

        if (SolarMaxMonthlyChart === null) {
            var ctx = (document.getElementById('SolarMaxYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                SolarMaxMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: solarMonth,
                    options: optionsA
                });
        }
        else {
            SolarMaxMonthlyChart.data = solarMonth;
            SolarMaxMonthlyChart.options = optionsA;
            SolarMaxMonthlyChart.update();
        }
    };

    const UvMaxMonthly = (data: IData) => {

        var uvMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var uvA: Array<number> = [];
        var count = 0;

        var yearMax = -100;
        var title = 'Max UV   ';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                uvA[el.month - 1] = el.maxUv;
                if (el.maxUv > yearMax)
                    yearMax = el.maxUv;
            });

            uvMonth.datasets.push({
                label: value.year.toString(),
                data: uvA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            title += value.year + ': ' + yearMax + '    ';

            count++;

            uvA = [];
            yearMax = -100;
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.6
        };

        if (UvMaxMonthlyChart === null) {
            var ctx = (document.getElementById('UvMaxYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                UvMaxMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: uvMonth,
                    options: optionsA
                });
        }
        else {
            UvMaxMonthlyChart.data = uvMonth;
            UvMaxMonthlyChart.options = optionsA;
            UvMaxMonthlyChart.update();
        }

    };

    const TempMaxMonthly = (data: IData) => {

        var tempMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var tempA: Array<number> = [];
        var count = 0;

        var yearMax = -100;
        var title = 'Max Temp   ';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                tempA[el.month - 1] = el.maxTemp;
                if (el.maxTemp > yearMax)
                    yearMax = el.maxTemp;
            });

            tempMonth.datasets.push({
                label: value.year.toString(),
                data: tempA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            title += value.year + ': ' + yearMax + '    ';

            count++;

            tempA = [];
            yearMax = -100;
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.6
        };

        if (TempMaxMonthlyChart === null) {
            var ctx = (document.getElementById('OutTempMaxYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                TempMaxMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: tempMonth,
                    options: optionsA
                });
        }
        else {
            TempMaxMonthlyChart.data = tempMonth;
            TempMaxMonthlyChart.options = optionsA;
            TempMaxMonthlyChart.update();
        }
    };

    const TempMinMaxMonthly = (data: IData) => {

        var tempMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var tempA: Array<number> = [];
        var count = 0;

        var yearMinMax = 100;
        var title = 'Min Max Temp   ';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                tempA[el.month - 1] = el.minMaxTemp;
                if (el.minMaxTemp < yearMinMax)
                    yearMinMax = el.minMaxTemp;
            });

            tempMonth.datasets.push({
                label: value.year.toString(),
                data: tempA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            title += value.year + ': ' + yearMinMax + '    ';

            count++;

            tempA = [];
            yearMinMax = 100;
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.6
        };

        if (TempMinMaxMonthlyChart === null) {
            var ctx = (document.getElementById('OutTempMinMaxYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                TempMinMaxMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: tempMonth,
                    options: optionsA
                });
        }
        else {
            TempMinMaxMonthlyChart.data = tempMonth;
            TempMinMaxMonthlyChart.options = optionsA;
            TempMinMaxMonthlyChart.update();
        }

    };

    const TempMinMonthly = (data: IData) => {

        var tempMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var tempA: Array<number> = [];
        var count = 0;

        var yearMin = 100;
        var title = 'Min Temp   ';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                tempA[el.month - 1] = el.minTemp;
                if (el.minTemp < yearMin)
                    yearMin = el.minTemp;
            });

            tempMonth.datasets.push({
                label: value.year.toString(),
                data: tempA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            title += value.year + ': ' + yearMin + '    ';

            count++;

            tempA = [];
            yearMin = 100;
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear'
                }
            },
            aspectRatio: 1.6
        };

        if (TempMinMonthlyChart === null) {
            var ctx = (document.getElementById('OutTempMinYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                TempMinMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: tempMonth,
                    options: optionsA
                });
        }
        else {
            TempMinMonthlyChart.data = tempMonth;
            TempMinMonthlyChart.options = optionsA;
            TempMinMonthlyChart.update();
        }
    };

    const TempMaxMinMonthly = (data: IData) => {

        var tempMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var tempA: Array<number> = [];
        var count = 0;

        var yearMaxMin = -100;
        var title = 'Max Min Temp   ';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                tempA[el.month - 1] = el.maxMinTemp;
                if (el.maxMinTemp > yearMaxMin)
                    yearMaxMin = el.maxMinTemp;
            });

            tempMonth.datasets.push({
                label: value.year.toString(),
                data: tempA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            title += value.year + ': ' + yearMaxMin + '    ';

            count++;

            tempA = [];
            yearMaxMin = -100;
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.6
        };

        if (TempMaxMinMonthlyChart === null) {
            var ctx = (document.getElementById('OutTempMaxMinYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                TempMaxMinMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: tempMonth,
                    options: optionsA
                });
        }
        else {
            TempMaxMinMonthlyChart.data = tempMonth;
            TempMaxMinMonthlyChart.options = optionsA;
            TempMaxMinMonthlyChart.update();
        }
    };

    const TempAvgMonthly = (data: IData) => {

        var tempMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var tempA: Array<number> = [];
        var count = 0;

        var title = 'Avg Temp';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                tempA[el.month - 1] = Math.round(el.avgTemp * 10) / 10;
            });

            tempMonth.datasets.push({
                label: value.year.toString(),
                data: tempA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            count++;

            tempA = [];
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.6
        };

        if (TempAvgMonthlyChart === null) {
            var ctx = (document.getElementById('OutTempAvgYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                TempAvgMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: tempMonth,
                    options: optionsA
                });
        }
        else {
            TempAvgMonthlyChart.data = tempMonth;
            TempAvgMonthlyChart.options = optionsA;
            TempAvgMonthlyChart.update();
        }

    };


    const TempAvgMaxMonthly = (data: IData) => {

        var tempMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var tempA: Array<number> = [];
        var count = 0;

        var title = 'Avg Max Temp';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                tempA[el.month - 1] = Math.round(el.avgMaxTemp * 10) / 10;
            });

            tempMonth.datasets.push({
                label: value.year.toString(),
                data: tempA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            count++;

            tempA = [];
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.6
        };

        if (TempAvgMaxMonthlyChart === null) {
            var ctx = (document.getElementById('OutTempAvgMaxYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                TempAvgMaxMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: tempMonth,
                    options: optionsA
                });
        }
        else {
            TempAvgMaxMonthlyChart.data = tempMonth;
            TempAvgMaxMonthlyChart.options = optionsA;
            TempAvgMaxMonthlyChart.update();
        }

    };


    const TempAvgMinMonthly = (data: IData) => {

        var tempMonth: ChartData<'bar', number[], number> = {
            labels: MonthLabels,
            datasets: []
        };

        var tempA: Array<number> = [];
        var count = 0;

        var title = 'Avg Min Temp';

        data.yearly.forEach(value => {
            value.months.forEach(el => {
                tempA[el.month - 1] = Math.round(el.avgMinTemp * 10) / 10;
            });

            tempMonth.datasets.push({
                label: value.year.toString(),
                data: tempA,
                backgroundColor: BackgroundColours[count],
                borderColor: BorderColours[count]
            });

            count++;

            tempA = [];
        });


        var optionsA: ChartOptions<'bar'> = {
            plugins: {
                title: {
                    display: true,
                    text: title
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    min: 0
                }
            },
            aspectRatio: 1.6
        };

        if (TempAvgMinMonthlyChart === null) {
            var ctx = (document.getElementById('OutTempAvgMinYear') as HTMLCanvasElement).getContext('2d');
            if (ctx)
                TempAvgMinMonthlyChart = new Chart(ctx, {
                    type: 'bar',
                    data: tempMonth,
                    options: optionsA
                });
        }
        else {
            TempAvgMinMonthlyChart.data = tempMonth;
            TempAvgMinMonthlyChart.options = optionsA;
            TempAvgMinMonthlyChart.update();
        }
    };

    return { InitAsync };

})();


document.addEventListener('DOMContentLoaded', async () => {
    await Weather.InitAsync();
});